let generatedId = 0;

export function createStableId(prefix) {
  generatedId += 1;
  return `${prefix}_${Date.now().toString(36)}_${generatedId.toString(36)}`;
}

export function createOption(label = "Nueva opción") {
  return { optionId: createStableId("option"), label };
}

export function createTableRow() {
  return { rowId: createStableId("row"), label: "Nueva fila" };
}

export function createTableColumn() {
  return {
    columnId: createStableId("column"),
    label: "Nueva columna",
    type: "shortText",
    required: false,
  };
}

export function createRepeaterColumn() {
  return {
    columnId: createStableId("column"),
    label: "Nueva columna",
    type: "shortText",
    required: false,
  };
}

// derivePublication calcula el Campo_Publicable de una pregunta pública
// automáticamente a partir de su propio label/type: el nombre de columna no
// es una decisión del administrador, es "la misma pregunta" (pedido
// explícito del usuario). Se recalcula cada vez que cambia label o type —
// ver QuestionEditor.vue::patchQuestion/changeType — así que nunca queda
// desincronizado del contenido de la pregunta.
//
// El sufijo del questionId va SIEMPRE al final del fieldId. Sin él, dos
// preguntas con el mismo label - el caso típico es duplicar una sección y
// no tocar el label de la copia - generarían el mismo fieldId, y publicar
// falla recién en el POST /publish con "fieldId publicable duplicado"
// (SchemaSignature, backend), sin haber pasado por /validate. El slug de
// label se mantiene solo para que el nombre de columna siga siendo legible.
export function derivePublication(question) {
  const slug = (question.label || "campo").trim().toLowerCase().normalize("NFD")
    .replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "campo";
  const suffix = String(question.questionId || "").split("_").slice(1).join("_");
  return {
    fieldId: suffix ? `campo_${slug}_${suffix}` : `campo_${slug}`,
    geoJsonType: question.type === "number" ? "number" : question.type === "check" ? "boolean" : "string",
    cardinality: question.type === "multiSelect" ? "many" : "one",
  };
}

export function createQuestion(type = "shortText") {
  const questionId = createStableId("question");
  const question = {
    questionId,
    type,
    label: "Nueva pregunta",
    help: "",
    required: false,
    // La capa de Terrain es el resultado esperado de una campaña: las
    // preguntas nacen publicables y el autor marca explícitamente Privado
    // solo aquello que no debe aparecer en mapa ni tabla.
    privacy: "public",
    validation: [],
  };
  question.publication = derivePublication(question);

  if (type === "singleSelect" || type === "multiSelect") question.options = [createOption("Opción 1")];
  if (type === "table") question.table = { rows: [], columns: [] };
  if (type === "repeater") question.repeater = { columns: [] };
  return question;
}

export function createSection() {
  return { sectionId: createStableId("section"), title: "Nueva sección", description: "", questions: [] };
}

export function cloneQuestion(question) {
  const duplicate = JSON.parse(JSON.stringify(question));
  duplicate.questionId = createStableId("question");
  // El fieldId depende del questionId (ver derivePublication) - hay que
  // recalcularlo con el questionId YA reasignado arriba, si no la copia se
  // queda con el fieldId original y colisiona con la pregunta fuente al
  // publicar.
  if (duplicate.publication) duplicate.publication = derivePublication(duplicate);
  if (duplicate.options) duplicate.options = duplicate.options.map((option) => ({ ...option, optionId: createStableId("option") }));
  if (duplicate.table) {
    duplicate.table.rows = (duplicate.table.rows || []).map((row) => ({ ...row, rowId: createStableId("row") }));
    duplicate.table.columns = (duplicate.table.columns || []).map((column) => ({ ...column, columnId: createStableId("column") }));
  }
  if (duplicate.repeater) duplicate.repeater.columns = (duplicate.repeater.columns || []).map((column) => ({ ...column, columnId: createStableId("column") }));
  return duplicate;
}

export function cloneSection(section) {
  const cloned = {
    ...JSON.parse(JSON.stringify(section)),
    sectionId: createStableId("section"),
    questions: (section.questions || []).map(cloneQuestion),
  };
  const questionIds = new Map((section.questions || []).map((question, index) => [question.questionId, cloned.questions[index].questionId]));
  const optionIds = new Map();
  (section.questions || []).forEach((question, questionIndex) => {
    (question.options || []).forEach((option, optionIndex) => optionIds.set(option.optionId, cloned.questions[questionIndex].options[optionIndex].optionId));
  });
  cloned.questions.forEach((question) => {
    if (!question.visibility || !questionIds.has(question.visibility.questionId)) return;
    question.visibility.questionId = questionIds.get(question.visibility.questionId);
    question.visibility.values = (question.visibility.values || []).map((value) => optionIds.get(value) || value);
  });
  return cloned;
}
